import { Component, Input, OnInit } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { SegmentService } from "@main-services/segment.service";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { CoreModule } from "@modules/core/core.module";
import { ProfileModel } from "../../profile/new-profile/entities/profile-model";
import { NewProfileFacade } from "../../profile/new-profile/state/new-profile.facade";
import { UKBusinessUserSelectorComponent } from "../../step-summary/sending-modal/uk-business-user-selector/uk-business-user-selector.component";
import { AddressBookWalletItemComponent } from "../address-book-wallet-item/address-book-wallet-item.component";
import { AddressBookWalletModel, AddressBookWalletViewType, OwnerType } from "../model/address-book-wallet.model";
import { AddressBookWalletFacade } from "../services/address-book-wallet.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { take } from "rxjs";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "wallets-list",
	templateUrl: "./address-book-wallets-list.component.html",
	styleUrls: ["./address-book-wallets-list.component.scss"],
	standalone: true,
	imports: [AddressBookWalletItemComponent, CoreModule]
})
export class AddressBookWalletsListComponent implements OnInit {
	@Input() selectedNetwork?: Network = null;
	@Input() selectedCryptocoin?: PaymentsCryptocoin = null;
	@Input() sendWalletId?: string = "";
	@Input() wallets: AddressBookWalletModel[] = [];
	@Input() set query(query: string) {
		this.queryText = query;
		this.myWallets = this.getWallets(true, query.toLocaleLowerCase());
		this.otherWallets = this.getWallets(false, query.toLocaleLowerCase());
	}
	@Input() set viewType(type: AddressBookWalletViewType) {
		this.modalType = type;
	}

	public myWallets: AddressBookWalletModel[] = [];
	public otherWallets: AddressBookWalletModel[] = [];
	public modalType: AddressBookWalletViewType = AddressBookWalletViewType.ADDRESS_BOOK;
	public queryText: string = "";
	public readonly addressBookWalletViewType = AddressBookWalletViewType;
	private isUKBusinessUser: boolean = false;
	private wallet?: AddressBookWalletModel;

	constructor(
		private readonly walletFacade: AddressBookWalletFacade,
		private readonly translationService: TranslationService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly profileFacade: NewProfileFacade,
		private readonly modalService: ModalService
	) {}

	ngOnInit(): void {
		this.profileFacade
			.getProfile$()
			.pipe(take(1))
			.subscribe((profile: ProfileModel) => {
				this.isUKBusinessUser = profile.iso === "GB" && profile.account_type !== "user";
			});
		this.walletFacade.onBehalfOf.pipe(untilDestroyed(this)).subscribe(() => this.goToSendFlow());
	}

	public editWallet(wallet: AddressBookWalletModel): void {
		this.wallet = wallet;
		if (this.modalType === AddressBookWalletViewType.WITHDRAW) {
			if (wallet.draft) {
				this.walletFacade.displayCreateWalletModal(
					AddressBookWalletViewType.WITHDRAW,
					wallet,
					this.sendWalletId,
					this.selectedNetwork,
					this.selectedCryptocoin
				);
			} else {
				this.handleSendFlow();
			}
			return;
		}

		if (this.modalType !== AddressBookWalletViewType.ADDRESS_BOOK) {
			return;
		}
		if (wallet?.draft) {
			this.walletFacade.displayCreateWalletModal(AddressBookWalletViewType.LEGACY, wallet);
		} else {
			this.viewWallet(wallet);
		}

		this.segmentService.track({
			name: "Address Book Wallet List Screen Viewed"
		});
		this.rudderstackService.track({
			name: "Address Book Wallet List Screen Viewed"
		});
	}

	public viewWallet(wallet: AddressBookWalletModel): void {
		this.wallet = wallet;
		if (this.modalType === AddressBookWalletViewType.WITHDRAW) {
			this.handleSendFlow();
			return;
		}

		if (this.modalType !== AddressBookWalletViewType.ADDRESS_BOOK) {
			return;
		}
		this.walletFacade.displayViewWalletModal(wallet);
	}

	private getWallets(isUserOwned: boolean, query: string): AddressBookWalletModel[] {
		return this.wallets.filter(
			({ draft, user_owned, owner_legal_name, wallet_address, wallet_host_name }: AddressBookWalletModel) =>
				this.isUserOwnedOrDraft(isUserOwned, user_owned, draft) &&
				(owner_legal_name?.toLowerCase().includes(query) ||
					wallet_host_name?.toLocaleLowerCase().includes(query) ||
					wallet_address.toLocaleLowerCase().includes(query))
		);
	}

	private handleSendFlow(): void {
		if (this.isUKBusinessUser && this.wallet.wallet_owner_type === OwnerType.PERSON) {
			const modal = this.modalService.create(UKBusinessUserSelectorComponent, {
				selectedNetwork: this.selectedNetwork,
				selectedCryptocoin: this.selectedCryptocoin
			});
			modal.present();
		} else {
			this.goToSendFlow();
		}
	}

	private isUserOwnedOrDraft(isUserOwned: boolean, user_owned: boolean | undefined, draft: boolean): boolean {
		if (user_owned !== undefined) {
			return user_owned === isUserOwned;
		}
		return draft !== isUserOwned;
	}

	public addNewWallet(): void {
		this.walletFacade.displayCreateWalletModal(AddressBookWalletViewType.NEW);
	}

	private goToSendFlow(): void {
		if (!this.wallet.wallet_host_name && this.wallet.wallet_host_type === "SELF_HOSTED") {
			this.wallet.wallet_host_name = this.translationService.translate("self_hosted_wallet");
		}
		this.walletFacade.validateWalletToSend(
			this.sendWalletId,
			this.wallet,
			this.selectedNetwork,
			this.selectedCryptocoin
		);
	}
}
